import { Toggle } from "../../components/Toggle";
import { t } from "../../i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, Plus, Save, Search, Trash2 } from "lucide-react";
import type { SkillDefinition } from "@lumen/shared";
import { api } from "../../api";
import {
  completePersonalizationRequestFromLocation,
  personalSkillPlanFromLocation,
} from "../../personalization";
import { SettingsShell } from "../../components/settings/SettingsShell";
export { McpPage } from "./McpPage";

type SkillDraft = {
  slug: string;
  name: string;
  description: string;
  instructions: string;
  enabled: boolean;
  scope: "platform" | "organization" | "personal";
};
const emptySkill: SkillDraft = {
  slug: "",
  name: "",
  description: "",
  instructions: "",
  enabled: true,
  scope: "personal",
};

export function SkillsPage({
  canManageOrganization,
  canManagePlatform,
  organizationId,
  canManageSiteOrganization = false,
}: {
  canManageOrganization: boolean;
  canManagePlatform: boolean;
  organizationId?: string;
  canManageSiteOrganization?: boolean;
}) {
  const [rows, setRows] = useState<SkillDefinition[]>([]);
  const [selected, setSelected] = useState<string>();
  const [query, setQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState<
    "all" | SkillDefinition["scope"]
  >("all");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState<SkillDraft>(() => ({
    ...emptySkill,
    scope: organizationId ? "organization" : "personal",
  }));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [planLoading, setPlanLoading] = useState(() =>
    new URLSearchParams(window.location.search).has("personalizationRequestId"),
  );
  const [planBlocked, setPlanBlocked] = useState(false);
  const [planMessage, setPlanMessage] = useState("");
  const [pendingCompletion, setPendingCompletion] = useState<string>();
  const [plannedSlug, setPlannedSlug] = useState<string>();
  useEffect(() => {
    let active = true;
    void personalSkillPlanFromLocation()
      .then((plan) => {
        if (!active || !plan) return;
        const values = plan.request;
        setPlannedSlug(values.slug);
        setDraft({
          ...emptySkill,
          slug: values.slug ?? "",
          name: values.name ?? "",
          description: values.description ?? "",
          instructions: values.instructions ?? "",
        });
        setPlanMessage(
          values.makeDefault
            ? "Review the proposed personal skill. Saving also selects it for future conversations."
            : "Review and save the proposed personal skill.",
        );
      })
      .catch((reason) => {
        if (!active) return;
        setPlanBlocked(true);
        setError(
          reason instanceof Error
            ? reason.message
            : "Could not load setup plan. Return to Personalization and retry.",
        );
      })
      .finally(() => {
        if (active) setPlanLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);
  const contextSuffix = organizationId
    ? `?organizationId=${encodeURIComponent(organizationId)}`
    : "";
  const load = useCallback(
    () =>
      api<SkillDefinition[]>(`/skills${contextSuffix}`)
        .then((nextRows) =>
          setRows(
            organizationId
              ? nextRows.filter((row) => row.scope === "organization")
              : nextRows,
          ),
        )
        .catch((reason) => setError(reason.message)),
    [contextSuffix, organizationId],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const choose = (row: SkillDefinition) => {
    if (pendingCompletion || planLoading || plannedSlug) return;
    setSelected(row.id);
    setDraft({
      slug: row.slug,
      name: row.name,
      description: row.description,
      instructions: row.instructions,
      enabled: row.enabled,
      scope: row.scope,
    });
    setConfirmDelete(false);
    setError("");
  };
  const fresh = () => {
    setSelected(undefined);
    setConfirmDelete(false);
    setDraft({
      ...emptySkill,
      scope: organizationId ? "organization" : "personal",
    });
    setError("");
  };
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (pendingCompletion) {
        await completePersonalizationRequestFromLocation({
          resourceType: "skill",
          resourceId: pendingCompletion,
        });
        setPendingCompletion(undefined);
        setPlannedSlug(undefined);
        setPlanMessage("Skill saved and setup completed.");
        await load();
        fresh();
        return;
      }
      const saved = await api<SkillDefinition>(
        `${selected ? `/skills/${selected}` : "/skills"}${contextSuffix}`,
        {
          method: selected ? "PATCH" : "POST",
          body: JSON.stringify(draft),
        },
      );
      if (!selected) {
        setPendingCompletion(saved.id);
        try {
          await completePersonalizationRequestFromLocation({
            resourceType: "skill",
            resourceId: saved.id,
          });
          setPendingCompletion(undefined);
          setPlannedSlug(undefined);
          setPlanMessage("");
        } catch (reason) {
          throw new Error(
            `Skill saved, but setup completion failed. Retry completion without creating another skill. ${reason instanceof Error ? reason.message : ""}`,
          );
        }
      }
      await load();
      if (!selected) fresh();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save skill",
      );
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!selected) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSaving(true);
    try {
      await api(`/skills/${selected}${contextSuffix}`, { method: "DELETE" });
      await completePersonalizationRequestFromLocation({
        resourceType: "skill",
        resourceId: selected,
      });
      fresh();
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not delete skill",
      );
    } finally {
      setSaving(false);
    }
  };
  const current = rows.find((row) => row.id === selected);
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (scopeFilter === "all" || row.scope === scopeFilter) &&
        (!needle ||
          row.slug.toLowerCase().includes(needle) ||
          row.name.toLowerCase().includes(needle) ||
          row.description.toLowerCase().includes(needle)),
    );
  }, [rows, query, scopeFilter]);
  const scopeCounts = useMemo(() => {
    const counts = {
      all: rows.length,
      platform: 0,
      organization: 0,
      personal: 0,
    };
    for (const row of rows) counts[row.scope] += 1;
    return counts;
  }, [rows]);
  return (
    <SettingsShell
      icon={BookOpen}
      title={t("Skills")}
      description="Reusable instructions invoked by typing /skill-id at the start of a message."
    >
      {planLoading && <p role="status">{t("Loading setup plan…")}</p>}
      {planMessage && (
        <p role="status" className="mb-4 text-sm">
          {planMessage}
        </p>
      )}
      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-[280px_1fr]">
        <div className="panel overflow-hidden">
          <div className="flex items-center justify-between border-b border-stone-200 p-3 dark:border-white/10">
            <span className="text-xs font-bold tracking-wider text-stone-400 uppercase">
              {t("Available skills")}
            </span>
            <span className="text-xs text-stone-400">
              {visibleRows.length}/{rows.length}
            </span>
            <button
              className="button p-2"
              onClick={fresh}
              disabled={
                saving ||
                planLoading ||
                Boolean(pendingCompletion) ||
                Boolean(plannedSlug)
              }
              title={t(
                organizationId
                  ? "New organization skill"
                  : "New personal skill",
              )}
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="flex items-center gap-2 border-b border-stone-200 px-3 py-2 dark:border-white/10">
            <div className="flex min-w-0 flex-1 items-center gap-1.5 rounded-lg bg-stone-100 px-2 dark:bg-white/5">
              <Search size={13} className="shrink-0 text-stone-400" />
              <input
                className="min-w-0 flex-1 bg-transparent py-1.5 text-xs"
                placeholder={t("Search skills")}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                aria-label={t("Search skills")}
              />
            </div>
            <select
              className="rounded-lg border border-stone-200 bg-transparent p-1.5 text-xs dark:border-white/10"
              value={scopeFilter}
              onChange={(event) =>
                setScopeFilter(event.target.value as typeof scopeFilter)
              }
              aria-label={t("Filter by scope")}
            >
              <option value="all">
                {t("All")} ({scopeCounts.all})
              </option>
              <option value="platform">
                {t("Platform")} ({scopeCounts.platform})
              </option>
              <option value="organization">
                {t("Organization")} ({scopeCounts.organization})
              </option>
              <option value="personal">
                {t("Personal")} ({scopeCounts.personal})
              </option>
            </select>
          </div>
          <div className="max-h-[65vh] overflow-y-auto p-2">
            {visibleRows.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-stone-400">
                {rows.length === 0
                  ? t("No skills yet.")
                  : t("No matching skills.")}
              </p>
            ) : null}
            {visibleRows.map((row) => (
              <button
                key={row.id}
                onClick={() => choose(row)}
                disabled={
                  saving ||
                  planLoading ||
                  Boolean(pendingCompletion) ||
                  Boolean(plannedSlug)
                }
                className={`w-full rounded-xl p-3 text-left ${selected === row.id ? "bg-lumen-50 dark:bg-lumen-900/30" : "hover:bg-stone-50 dark:hover:bg-white/5"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <code className="text-sm font-bold text-lumen-700 dark:text-lumen-300">
                    /{row.slug}
                  </code>
                  <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] text-stone-500 uppercase dark:bg-white/10">
                    {row.scope}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-stone-500">
                  {row.description || row.name}
                </p>
              </button>
            ))}
          </div>
        </div>
        <div className="panel p-5">
          {current && !current.editable ? (
            <div>
              <h2 className="text-lg font-bold">{current.name}</h2>
              <p className="mt-2 text-sm text-stone-500">
                {current.description}
              </p>
              <pre className="mt-5 rounded-xl bg-stone-50 p-4 text-sm whitespace-pre-wrap dark:bg-white/5">
                {current.instructions}
              </pre>
              <p className="mt-4 text-xs text-stone-400">
                {t("This workspace skill is managed by an administrator.")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <fieldset
                disabled={
                  saving ||
                  planLoading ||
                  planBlocked ||
                  Boolean(pendingCompletion)
                }
                className="contents"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <label>
                    <span className="label">{t("Slash ID")}</span>
                    <div className="flex items-center rounded-xl border border-stone-200 px-3 dark:border-white/10">
                      <span className="text-stone-400">/</span>
                      <input
                        className="min-w-0 flex-1 bg-transparent px-1 py-2.5 text-sm"
                        value={draft.slug}
                        readOnly={Boolean(plannedSlug)}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            slug: event.target.value
                              .toLowerCase()
                              .replace(/[^a-z0-9-]/g, ""),
                          })
                        }
                      />
                    </div>
                  </label>
                  <label>
                    <span className="label">{t("Display name")}</span>
                    <input
                      className="field"
                      value={draft.name}
                      onChange={(event) =>
                        setDraft({ ...draft, name: event.target.value })
                      }
                    />
                  </label>
                </div>
                <label>
                  <span className="label">{t("Description")}</span>
                  <input
                    className="field"
                    value={draft.description}
                    onChange={(event) =>
                      setDraft({ ...draft, description: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span className="label">{t("Instructions")}</span>
                  <textarea
                    className="field min-h-52 font-mono"
                    value={draft.instructions}
                    onChange={(event) =>
                      setDraft({ ...draft, instructions: event.target.value })
                    }
                  />
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Toggle
                      checked={draft.enabled}
                      disabled={Boolean(plannedSlug)}
                      onChange={(event) =>
                        setDraft({ ...draft, enabled: event.target.checked })
                      }
                    />{" "}
                    {t("Enabled")}
                  </label>
                  {!selected &&
                    !plannedSlug &&
                    (canManageOrganization ||
                      canManagePlatform ||
                      canManageSiteOrganization) && (
                      <label className="flex items-center gap-2 text-sm">
                        <span>{t("Scope")}</span>
                        <select
                          className="field py-2"
                          value={draft.scope}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              scope: event.target.value as SkillDraft["scope"],
                            })
                          }
                        >
                          <option value="personal">{t("Only me")}</option>
                          {(canManageOrganization ||
                            canManageSiteOrganization) && (
                            <option value="organization">
                              {t("This organization")}
                            </option>
                          )}
                          {canManagePlatform && (
                            <option value="platform">{t("Entire site")}</option>
                          )}
                        </select>
                      </label>
                    )}
                </div>
              </fieldset>
              <div className="flex justify-end gap-2">
                {selected && (
                  <button
                    className={`button ${confirmDelete ? "bg-red-600 text-white" : "text-red-600"}`}
                    disabled={saving}
                    onClick={() => void remove()}
                    onBlur={() => setConfirmDelete(false)}
                  >
                    <Trash2 size={16} />
                    {confirmDelete ? t("Confirm delete") : t("Delete")}
                  </button>
                )}
                <button
                  className="button-primary"
                  disabled={
                    saving ||
                    planLoading ||
                    planBlocked ||
                    !draft.slug ||
                    !draft.name ||
                    !draft.instructions
                  }
                  onClick={() => void save()}
                >
                  <Save size={16} />{" "}
                  {pendingCompletion
                    ? t("Retry setup completion")
                    : t("Save skill")}
                </button>
              </div>
            </div>
          )}
          {error && (
            <p role="alert" className="mt-4 text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
      </div>
    </SettingsShell>
  );
}
